import * as THREE from 'three';
import { SceneNode } from '../core/SceneNode';
import { Vector3, Quaternion } from '../types';
import { InterpolatedProperty } from '../properties/InterpolatedProperty';
import { AppSettingsData } from '../core/AppSettingsData';
import { Transform, TransformPropertyMode } from '../core/Transform';
import { InputEventManager } from '../core/InputEventManager';


export type CameraType = 'perspective' | 'orthographic' | 'orbit' | 'fixed_offset' | 'poi_camera';

export abstract class Camera extends SceneNode {
    public readonly fov: InterpolatedProperty<number>;
    public readonly near: InterpolatedProperty<number>;
    public readonly far: InterpolatedProperty<number>;
    public readonly cameraType: CameraType;


    protected _appSettings: AppSettingsData ;
    private _isActive: boolean = false;
    private threeCamera: THREE.PerspectiveCamera | null = null;

    constructor(
        name: string,
        cameraType: CameraType,
        appSettings: AppSettingsData,
        fov: number = 75,
        near: number = 0.1,
        far: number = 1000,
        transform?: Transform
    ) {
        // Use the provided transform, or create a default 'simple' one.
        const finalTransform = transform || new Transform(
            Vector3.zero(),
            Quaternion.identity(),
            Vector3.one(),
            TransformPropertyMode.SIMPLE
        );
        super(name, finalTransform);
        
        this._appSettings = appSettings;
        this.cameraType = cameraType;
        const group = 'Camera';
        this.fov = new InterpolatedProperty('FOV', fov, fov, 0.5, group).withBounds(10, 120, 1);
        this.near = new InterpolatedProperty('Near Plane', near, near, 0.1, group).withBounds(0.01, 10, 0.01);
        this.far = new InterpolatedProperty('Far Plane', far, far, 0.1, group).withBounds(1, 10000, 1);
    }

    /**
     * The direction the camera is looking, derived from its rotation.
     */
    public get direction(): Vector3 {
        return this.transform.getForwardVector();
    }

    /**
     * The 'up' vector of the camera, derived from its rotation.
     */
    public get up(): Vector3 {
        return this.transform.getUpVector();
    }

    public setRenderCamera(cam: THREE.PerspectiveCamera): void {
        this.threeCamera = cam;
    }

    setActive(active: boolean): void {
        if (this._isActive === active) return;
        this._isActive = active;
        if (active) {
            this.onActivated();
        } else {
            this.onDeactivated();
        }
    }

    isActive(): boolean {
        return this._isActive;
    }

    /**
     * Subclasses can override this method to update their state based on user input.
     * The default implementation is empty. Input should be polled from the 
     * `InputEventManager.Instance`.
     * 
     * @param deltaTime The time elapsed since the last frame.
     */
    public updateControls(deltaTime: number): void {
        // To be overridden by cameras with user controls (e.g., FlyCamera).
    }

    public syncToThree(threeCamera: THREE.PerspectiveCamera): void {
        const position = this.transform.getWorldPosition();
        const rotation = this.transform.getWorldQuaternion();

        // The transform's rotation (quaternion) is now the single source of truth.
        threeCamera.position.copy(position.toThree());
        threeCamera.quaternion.copy(rotation.toThree());

        threeCamera.fov = this.fov.value ?? 75;
        threeCamera.near = this.near.value ?? 0.1;
        threeCamera.far = this.far.value ?? 1000;
        threeCamera.updateProjectionMatrix();
    }

    protected onUpdate(deltaTime: number): void {
        super.onUpdate(deltaTime);
        if (this._isActive) {
            this.updateControls(deltaTime);
        }
        if (this.threeCamera) {
            this.syncToThree(this.threeCamera);
        }
    }

    public setPreset(preset: CameraPreset): void {
        const position = new Vector3();
        let fov = 75;
    
        switch (preset) {
            case 'default':
                position.set(0, 5, 10);
                fov = 75;
                break;
            case 'cinematic':
                position.set(15, 8, 15);
                fov = 35;
                break;
            case 'wide':
                position.set(0, 10, 20);
                fov = 90;
                break;
            case 'close':
                position.set(0, 2, 5);
                fov = 50;
                break;
        }
    
        this.transform.position.value = position;
        
        // Berechne die Richtung und setze die Orientierung des Transforms
        const direction = Vector3.zero().subtract(position).normalize();
        this.transform.setOrientation(direction, Vector3.up());
    
        this.fov.value = fov;
    }

    public shake(intensity: number = 1.0, duration: number = 0.5): void {
        const originalPosition = this.transform.position.value;
        if (!originalPosition) return;
        
        const clonedPosition = originalPosition.clone();
        
        const shakeOffset = new Vector3(
            (Math.random() - 0.5) * intensity,
            (Math.random() - 0.5) * intensity,
            (Math.random() - 0.5) * intensity
        );
        
        this.transform.position.value = clonedPosition.add(shakeOffset);
        
        setTimeout(() => {
            if (this.transform.position.value) {
                 this.transform.position.value = originalPosition;
            }
        }, 100);
    }

    protected onActivated(): void {}
    protected onDeactivated(): void {}
    
    protected calculateBoundingBox(): THREE.Box3 {
        return new THREE.Box3();
    }
}

export type CameraPreset = 'default' | 'cinematic' | 'wide' | 'close';